import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import clsx from 'clsx';

import { useUserConfig } from '../../../context/UserConfigContext';
import { useElementWidth } from '../../../hooks/useElementWidth';
import { useGetExerciseOptions } from '../../../redux/api/exerciseConfigAPI';
import {
  ExerciseOptionType,
  setsColumnWidth,
  sideColumnWidth,
  typeColumnWidth,
} from '../../../shared/constants';
import { IProgramSetItem } from '../../../types/interfaces/program-set-item';
import { ITrainingSetItem } from '../../../types/interfaces/training-set-item';
import { getTimeValueFromOptionValue } from '../../../utils/datetime/date-time-utils';
import {
  getColumnOptions,
  getColumnWidth,
} from '../../../utils/ui/column-size-utils';
import SaveActionButton from '../../designe/ActionButtons/SaveActionButton';
import AppDialog from '../app-dialog/AppDialog';
import SaveSet from '../set-group-edit-table/SetEdit/save-set/SaveSet';
import SetTypeIcon from '../set-type-icon/SetTypeIcon';
import { useStyles } from './SetOptionTable.styles';
import {
  ISetOptionTableProps,
  SetOptionTableFieldName,
  SetOptionTableMode,
} from './interfaces';

export default function SetOptionTable({
  set,
  mode,
  valueProperty,
  showSetsColumn = false,
  showRestColumn = false,
  canSaveSet = false,
  onSaveSet,
}: ISetOptionTableProps) {
  const { ref, width } = useElementWidth();
  const theme = useTheme();

  const { classes } = useStyles(theme);
  const { t } = useTranslation();
  const { data: exerciseOptions } = useGetExerciseOptions();
  const [showSaveSetModal, setShowSaveSetModal] = useState(false);
  const { options: userConfigOptions } = useUserConfig();
  const showExerciseUnits = userConfigOptions.showExerciseUnits;

  const fieldNames = SetOptionTableFieldName[mode];
  const property = valueProperty || fieldNames.optionFieldName;
  const property2 = `${property}2`;

  const sideLabel = {
    [property]: t('exercises.rightShort'),
    [property2]: t('exercises.leftShort'),
  };

  const options = useMemo(() => {
    if (!set.exercise) return [];

    const options = [
      ...(set.exercise.options.map((eo) =>
        exerciseOptions?.find((o) => eo.id === o.id),
      ) || []),
    ].filter(Boolean);

    options.sort((a, b) => a.priorityOrder - b.priorityOrder);

    return options;
  }, [set]);

  const setItems: ITrainingSetItem[] | IProgramSetItem[] = useMemo(
    () => set[fieldNames.setItemsFieldName] || [],
    [set, fieldNames.setItemsFieldName],
  );

  const columnWidth = getColumnWidth(
    width,
    options?.length + (showRestColumn ? 1 : 0),
    typeColumnWidth +
      (set.exercise?.splitBySide ? sideColumnWidth : 0) +
      (showSetsColumn ? setsColumnWidth : 0),
  );

  const getOptionValue = useCallback((option, value) => {
    if (option.type === ExerciseOptionType.time) {
      return getTimeValueFromOptionValue(value, t);
    }

    return value;
  }, []);

  const renderOptionHeader = useCallback(() => {
    return (
      <Box className={classes.setItemListHeader} display="flex">
        <Box
          {...getColumnOptions(typeColumnWidth)}
          className={classes.headerTextWrapper}>
          <Typography variant="label-text-md">{t('common.type')}</Typography>
        </Box>
        {showSetsColumn && (
          <Box
            {...getColumnOptions(setsColumnWidth)}
            className={classes.headerTextWrapper}>
            <Typography variant="label-text-md">{t('common.sets')}</Typography>
          </Box>
        )}
        {showRestColumn && (
          <Box
            {...getColumnOptions(columnWidth)}
            className={classes.headerTextWrapper}>
            <Typography variant="label-text-md">{t('common.rest')}</Typography>
          </Box>
        )}
        {set.exercise?.splitBySide && (
          <Box
            {...getColumnOptions(sideColumnWidth)}
            className={classes.headerTextWrapper}></Box>
        )}
        {options.map((option) => (
          <Box
            key={option.id}
            {...getColumnOptions(columnWidth)}
            className={classes.headerTextWrapper}>
            <Typography variant="label-text-md">{option.name}</Typography>
          </Box>
        ))}
      </Box>
    );
  }, [options, columnWidth]);

  const renderOptionRow = useCallback(
    (item: ITrainingSetItem | IProgramSetItem, secondOption = false) => {
      const fieldValue = !secondOption ? property : property2;
      return (
        <Box display="flex">
          {set.exercise?.splitBySide && (
            <Box {...getColumnOptions(sideColumnWidth)}>
              <Typography variant="label-text-md">
                {sideLabel[fieldValue]}
              </Typography>
            </Box>
          )}
          {options.map((option) => (
            <Box
              key={option.id}
              {...getColumnOptions(columnWidth)}
              paddingX={1}>
              <Typography variant="label-text-md">
                {getOptionValue(
                  option,
                  item?.[fieldNames.optionsFieldName]?.find(
                    (o) => o.optionId === option.id,
                  )?.[fieldValue],
                )}
                {option.type !== ExerciseOptionType.time &&
                  showExerciseUnits && (
                    <Typography variant="label-text-sm" fontSize="10px">
                      {exerciseOptions?.find((o) => o.id === option.id)?.unit}
                    </Typography>
                  )}
              </Typography>
            </Box>
          ))}
        </Box>
      );
    },
    [options, set, columnWidth, property, property2, sideLabel, fieldNames],
  );

  return (
    <>
      <Box ref={ref} className={classes.setItemTableWrapper}>
        {canSaveSet && onSaveSet && set.exerciseId && (
          <Box className={classes.saveSetButton}>
            <SaveActionButton onClick={() => setShowSaveSetModal(true)} />
          </Box>
        )}
        <Box key={set.id}>
          {renderOptionHeader()}

          {setItems.map(
            (item: ITrainingSetItem | IProgramSetItem, index: number) => (
              <Box
                key={item.id}
                display="flex"
                alignItems="center"
                className={clsx(
                  { [classes.oddRow]: index % 2 === 1 },
                  classes.setItemListRow,
                )}>
                <Box {...getColumnOptions(typeColumnWidth)} paddingLeft={1}>
                  <SetTypeIcon type={item.type} />
                </Box>
                {showSetsColumn && 'sets' in item && (
                  <Box {...getColumnOptions(setsColumnWidth)} paddingX={1}>
                    <Typography variant="label-text-md">{item.sets}</Typography>
                  </Box>
                )}
                {showRestColumn && (
                  <Box {...getColumnOptions(columnWidth)} paddingX={1}>
                    <Typography variant="label-text-md">
                      {getTimeValueFromOptionValue(item.rest, t)}
                    </Typography>
                  </Box>
                )}

                <Box display="flex" flexDirection="column">
                  <Box display="flex" alignItems="center">
                    {renderOptionRow(item)}
                  </Box>
                  {set.exercise?.splitBySide && (
                    <Box display="flex" alignItems="center">
                      {renderOptionRow(item, true)}
                    </Box>
                  )}
                </Box>
              </Box>
            ),
          )}
        </Box>
      </Box>

      {canSaveSet && onSaveSet && set.exerciseId && (
        <AppDialog
          open={showSaveSetModal}
          onClose={() => setShowSaveSetModal(false)}
          title={t('setTable.saveSet')}>
          <SaveSet
            onCancel={() => setShowSaveSetModal(false)}
            onSaveSet={(title) => {
              return onSaveSet(title).then(() => {
                setShowSaveSetModal(false);
              });
            }}
          />
        </AppDialog>
      )}
    </>
  );
}
